import { dayjs } from "@/core/utils/date";
import type { CSVValidationError } from "./csv-validation";
import { validateCSV, convertToCSV } from "./csv-validation";

export const TECHNICIAN_REQUIRED_COLUMNS = ["Email"];
export const TECHNICIAN_PHONE_COLUMNS = ["Phone Number"];

export interface TechnicianCSVValidationResult {
  isValid: boolean;
  errors: CSVValidationError[];
  normalizedData?: string[][];
}

/**
 * Validates a CSV file for technician import with required columns and phone number format
 * @param file - The CSV file to validate
 * @returns Promise with validation result
 */
export const validateTechnicianCSVFile = async (
  file: File,
): Promise<TechnicianCSVValidationResult> =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      let content = e.target?.result as string;
      content = content.replace(/^\uFEFF/, "");
      content = content.replace(/\r\n?/g, "\n");
      const validation = validateCSV(content, {
        requiredColumns: TECHNICIAN_REQUIRED_COLUMNS,
        phoneNumberColumns: TECHNICIAN_PHONE_COLUMNS,
      });
      resolve({
        isValid: validation.isValid,
        errors: validation.errors,
        normalizedData: validation.normalizedData,
      });
    };
    reader.onerror = () => {
      resolve({
        isValid: false,
        errors: [
          {
            row: 1,
            column: "file",
            message: "Failed to read file",
            value: file.name,
          },
        ],
      });
    };
    reader.readAsText(file);
  });

/**
 * Processes a validated CSV file and returns a normalized File object
 * @param file - The original file
 * @param validation - The validation result
 * @returns Normalized File object or the original file
 */
export const processValidatedTechnicianCSV = (
  file: File,
  validation: TechnicianCSVValidationResult,
): File => {
  if (validation.normalizedData) {
    const csv = convertToCSV(validation.normalizedData);
    return new File([csv], file.name.replace(/\.(xlsx?|csv)$/i, ".csv"), {
      type: "text/csv",
      lastModified:
        typeof file.lastModified === "number"
          ? file.lastModified
          : dayjs().valueOf(),
    });
  }
  return file;
};
